import { useCallback, useRef } from "react";

import useThrottle from "@/app/hooks/useThrottle";
import type {
  EventCallback,
  EventData,
} from "@/features/map/hooks/events/types";

/**
 * This hook stores all incremental displacement in drag events, allowing
 * you to throttle the update function, and having access to the summatory
 * of all displacements since last call
 */
export const useThrottledDragEvent = ({
  callback,
  delay,
  manuallyResetData = false,
}: {
  callback: EventCallback;
  delay: number;
  manuallyResetData?: boolean;
}) => {
  // Keep a sum of the displacement since last throttled callback run
  const totalDisplacement = useRef<{ lat: number; lng: number }>({
    lat: 0,
    lng: 0,
  });

  const _resetData = useCallback(() => {
    totalDisplacement.current = {
      lat: 0,
      lng: 0,
    };
  }, []);

  // Throttle the user's callback
  const throttleUserCallback = useThrottle((data: EventData) => {
    const returnValue = callback({
      ...data,
      displacement: totalDisplacement.current,
    });

    // Reset sum of displacement
    if (!manuallyResetData) {
      _resetData();
    }

    return returnValue;
  }, delay);

  const _onDrag = useCallback(
    (eventData: EventData) => {
      // Sum all displacements until we run user's callback function
      totalDisplacement.current = {
        lat:
          totalDisplacement.current.lat + (eventData?.displacement?.lat ?? 0),
        lng:
          totalDisplacement.current.lng + (eventData?.displacement?.lng ?? 0),
      };
      return throttleUserCallback(eventData);
    },
    [throttleUserCallback, totalDisplacement?.current],
  );

  return {
    displacementRef: totalDisplacement,
    reset: _resetData,
    throttledDragEvent: _onDrag,
  };
};
